import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import { SectionHeading } from "components/misc/Headings";
import { ReactComponent as SvgDotPatternIcon } from "images/dot-pattern.svg";
import CodeReview from "images/business/code-review.svg";
import Building from "images/business/building.svg";
import Teaching from "images/business/teaching.svg";
import InternationalMarket from "images/business/international-market.svg";
import BusinessOverViewMobileView from "./BusinessOverViewMobileView";

const HeadingContainer = tw.div`text-center`;
const Heading = tw(SectionHeading)`text-ncc`;

const Posts = tw.div`mt-4 flex flex-wrap -mr-3 relative`;
const PostContainer = styled.div`
  ${tw`relative z-20 mt-10 sm:pt-3 pr-3 w-full sm:w-1/3 lg:w-1/4 max-w-sm mx-auto sm:max-w-none sm:mx-0`}
`;

const Post = styled.a`
  ${tw`flex flex-col h-full bg-gray-100 rounded-lg cursor-pointer hover:cursor-pointer`}
  border: ${(props) =>
    props.firstCard
      ? "1px solid rgba(12, 77, 150, 0.22)" // Solid border for the first card
      : "1px dashed rgba(12, 77, 150, 0.22)"}; // Dashed border for the rest
`;

const PostImage = styled.div`
  ${(props) =>
    css`
      background-image: url("${props.imageSrc}");
    `}
  ${tw`h-40 sm:h-40 bg-center bg-cover rounded-t`}
`;

const PostText = tw.div`
  flex-1 px-6 py-8 bg-[#eeeeee70]
`;
const PostTitle = tw.h6`font-bold group-hocus:text-ncc text-ncc transition duration-300`;
const PostDescription = tw.p`text-sm text-gray-600 leading-relaxed mt-2`;

const DecoratorBlob1 = tw(
  SvgDotPatternIcon
)`absolute bottom-0 left-0 w-32 h-32 mb-3 ml-3 transform -translate-x-1/2 translate-y-1/2 fill-current text-gray-500 opacity-50`;
const DecoratorBlob2 = tw(
  SvgDotPatternIcon
)`absolute top-0 right-0 w-32 h-32 mt-16 mr-6 transform translate-x-1/2 -translate-y-1/2 fill-current text-gray-500 opacity-50`;

export default ({
  businessIntroRef,
  systemEngRef,
  reinforcementRef,
  businessSysDevRef,
  leaseRef,
  chineseMarketRef,
  isXs = false,
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  const scrollToTarget = (targetRef) => {
    if (targetRef.current) {
      const offset = 100; // Add an offset of 100px to avoid content disappearing
      const targetPosition =
        targetRef.current.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const hash = location.hash;
    if (hash === "#system-engineering") {
      scrollToTarget(systemEngRef);
    } else if (hash === "#reinforcement-system") {
      scrollToTarget(reinforcementRef);
    } else if (hash === "#business-development") {
      scrollToTarget(businessSysDevRef);
    } else if (hash === "#lease-business") {
      scrollToTarget(leaseRef);
    } else if (hash === "#chinese-market") {
      scrollToTarget(chineseMarketRef);
    }
  }, [
    location,
    systemEngRef,
    reinforcementRef,
    businessSysDevRef,
    leaseRef,
    chineseMarketRef,
  ]);

  const posts = [
    {
      postImageSrc: CodeReview,
      title: t("business.ses.heading"),
      description: t("business.ses.description"),
      reference: systemEngRef,
    },
    {
      postImageSrc: Building,
      title: t("business.reinforcement.heading"),
      description: t("business.reinforcement.description"),
      reference: reinforcementRef,
    },
    {
      postImageSrc: Teaching,
      title: t("business.lease-of-pc.heading"),
      description: t("business.lease-of-pc.description"),
      reference: leaseRef,
    },
    {
      postImageSrc: InternationalMarket,
      title: t("business.chineseMarketSupport.heading"),
      description: t("business.chineseMarketSupport.description"),
      reference: chineseMarketRef,
    },
  ];

  return (
    <Container>
      <ContentWithPaddingXl>
        {isXs ? (
          <BusinessOverViewMobileView
            {...{
              businessIntroRef,
              systemEngRef,
              reinforcementRef,
              businessSysDevRef,
              leaseRef,
              chineseMarketRef,
            }}
          />
        ) : (
          <>
            <HeadingContainer>
              <Heading>{t("business.subheading")}</Heading>
            </HeadingContainer>
            <Posts>
              {posts.map((post, index) => (
                <PostContainer key={index}>
                  <Post
                    className="group"
                    onClick={() => scrollToTarget(post.reference)}
                  >
                    <PostImage imageSrc={post.postImageSrc} />
                    <PostText>
                      <PostTitle>{post.title}</PostTitle>
                      <PostDescription>{post.description}</PostDescription>
                    </PostText>
                  </Post>
                </PostContainer>
              ))}
            </Posts>
            <DecoratorBlob1 />
            <DecoratorBlob2 />
          </>
        )}
      </ContentWithPaddingXl>
    </Container>
  );
};
